import { IDisplayedSlice } from '../../interfaces';
import * as ChartGroupsUI from './ChartGroupsUI';
import AliasEditorWrapper from '../../../AliasEditor/AliasEditorWrapper';
import { useCallback, useMemo, useState } from 'react';
import { createAlias } from './utils';
import { appendJumpToOptions } from '../../../AliasEditor/utils';
import { IAlias, IAliasOption } from '../../../AliasEditor';
import { GraphQLUserAliasId } from '../../../../utils/types';
import useCreateCohortFromChartGroup from './useCreateCohortFromChartGroup';
import { useAliasNavigation } from '../../../Builder/AliasNavigation/hooks';
import { useChartContext } from '../../ChartContext';
import { useFullscreenContext } from '../../../../utils/contexts/FullscreenContext';
import { SUBJECT_EVENT_KEY } from '../../constants/utils';
import {
  ICohortRefFieldsFragment,
  PermissionKey,
  QueryType,
  SimpleCohortTypeKey,
} from '../../../../utils/apollo/graphqlSchema';
import { DateKindKey } from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch } from '../../../../redux/hooks';
import { customizeSliceColorAction } from '../../../../redux/slices/analyses';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import ContextualMenuSubItemWrapper from '../../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import ColorMenu from '../ColorMenu';
import { IBreakdownOverallStatus } from '../../../../utils/interfaces';
import { getSliceColorFromColorConfig } from '../../utils/analysisColorConfig';
import usePermission from '../../../../utils/hooks/permissionHooks/usePermission';
import checkCompoundCohortByHint from '../../../../utils/createCohort/checkCompoundCohortByHint';
import { Dispatch } from '@reduxjs/toolkit';
import { colorMenuStyles } from '../ColorMenu/ColorMenu';

interface IProps {
  children: React.ReactNode;
  slice: IDisplayedSlice;
}

const ChartGroupWrapper = ({ children, slice }: IProps) => {
  const dispatch = useAppDispatch();
  const {
    metadata: { analysisId, query, queryType, isBreakdownOverall, isEmbedded },
    colorConfig,
  } = useChartContext();

  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);

  const { checkIsPermitted } = usePermission();
  const { isFullScreen, toggleFullScreen } = useFullscreenContext();
  const handleCreateCohort = useCreateCohortFromChartGroup(analysisId);
  const { currentAliasAnchors, highlight } = useAliasNavigation();

  const isPermittedToCreateAnalysis = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AnalysisCreate],
    schemaId: query?.schema.id,
  });

  const breakdownCohorts: ICohortRefFieldsFragment[] = useMemo(
    () =>
      'breakdown' in query && query.breakdown !== null
        ? query.breakdown.cohortRefs
        : [],
    [query],
  );

  const handleJumpTo = (aliasId: GraphQLUserAliasId) => {
    if (isFullScreen) {
      toggleFullScreen();
    }

    highlight(aliasId);
  };

  const allowCreateCohort = useMemo(() => {
    // Only analysis with "Event" date is supported in cohort
    const dateKindCheck =
      'dateKind' in query && query.dateKind.key === DateKindKey.Event;

    if (queryType === QueryType.Funnel) {
      // funnels with Event subject are not supported
      return (
        dateKindCheck &&
        'subject' in query &&
        query.subject?.key !== SUBJECT_EVENT_KEY
      );
    }

    return dateKindCheck;
  }, [query, queryType]);

  const handleChangeSliceColor = useCallback(
    (sliceKey: string) => (customColor: string) => {
      dispatch(
        customizeSliceColorAction({
          analysisId,
          sliceKey,
          customColor,
        }),
      );
    },
    [analysisId, dispatch],
  );

  const getExtraMenuItems = useCallback(
    (slice: IDisplayedSlice) => {
      const result: IContextualMenuItem[] = [];
      if (
        (queryType === QueryType.Expression ||
          queryType === QueryType.Funnel ||
          queryType === QueryType.Retention) &&
        isBreakdownOverall !== IBreakdownOverallStatus.NoBreakdownCompare
      ) {
        const currentColor = getSliceColorFromColorConfig(
          colorConfig,
          slice.key,
        );

        result.push({
          key: 'custom-group-color',
          onRenderContent: () => (
            <ContextualMenuOption
              label={'Change Color'}
              hasSubMenus={true}
              iconName="palette"
            />
          ),
          subMenuProps: {
            styles: colorMenuStyles,
            items: [
              {
                key: 'custom-group-color-menu',
                onRender: () => (
                  <ContextualMenuSubItemWrapper>
                    <ColorMenu
                      currentColor={currentColor}
                      onSubmit={handleChangeSliceColor(slice.key)}
                    />
                  </ContextualMenuSubItemWrapper>
                ),
              },
            ],
          },
        });
      }

      if (
        allowCreateCohort &&
        (queryType === QueryType.Expression ||
          queryType === QueryType.Funnel) &&
        !slice.isComparison &&
        !isEmbedded
      ) {
        const isCompoundCohort = checkCompoundCohortByHint(
          slice.originalHint,
          breakdownCohorts,
        );

        // compound cohorts are not allowed
        if (!isCompoundCohort && !query.schema.disabled) {
          const type =
            queryType === QueryType.Funnel
              ? SimpleCohortTypeKey.Funnel
              : SimpleCohortTypeKey.Expression;

          result.push(
            {
              key: 'create-cohort',
              onRenderContent: () => (
                <ContextualMenuOption
                  label={'Create Cohort'}
                  iconName="users-three"
                  onClick={() =>
                    handleCreateCohort({
                      slice,
                      type,
                      isExecuteAfterRedirect: false,
                    })
                  }
                  isUnPermitted={!isPermittedToCreateAnalysis}
                />
              ),
            },
            {
              key: 'inspect-users',
              onRenderContent: () => (
                <ContextualMenuOption
                  label={'Inspect Users'}
                  iconName="user-circle"
                  onClick={() =>
                    handleCreateCohort({
                      slice,
                      type,
                      isExecuteAfterRedirect: true,
                    })
                  }
                  isUnPermitted={!isPermittedToCreateAnalysis}
                />
              ),
            },
          );
        }
      }

      return result;
    },
    [
      queryType,
      isBreakdownOverall,
      allowCreateCohort,
      colorConfig,
      handleChangeSliceColor,
      breakdownCohorts,
      isPermittedToCreateAnalysis,
      handleCreateCohort,
      isEmbedded,
      query,
    ],
  );

  function createAliases(
    analysisId: string,
    slice: IDisplayedSlice,
    aliasAnchors: Array<GraphQLUserAliasId>,
    dispatch: Dispatch,
    jumpTo: (aliasId: GraphQLUserAliasId) => void,
  ): IAliasOption[] {
    const userAlias = slice.userAlias;

    const aliases: IAlias[] = [];

    if (
      // adding exclude rule for debug page since we are not able to ready userAlias from other envs.
      window.location.pathname.includes('debug') ||
      userAlias.__typename === 'UserAliasSliceNone'
    ) {
      return aliases;
    }

    // add rename option for base and breakdown slices
    if (
      userAlias.__typename === 'UserAliasSliceBase' ||
      userAlias.__typename === 'UserAliasSliceBreakdown'
    ) {
      aliases.push(
        createAlias(analysisId, userAlias.aliasId, slice.name, dispatch),
      );
    }
    appendJumpToOptions(aliases, aliasAnchors, jumpTo);
    return aliases;
  }

  return (
    <ChartGroupsUI.DropdownContainer $isDropdownOpen={isGroupDropdownOpen}>
      <AliasEditorWrapper
        aliases={createAliases(
          analysisId,
          slice,
          currentAliasAnchors,
          dispatch,
          handleJumpTo,
        )}
        inline={true}
        disabled={!slice.isVisible}
        extraMenuItems={getExtraMenuItems(slice)}
        setIsMenuOpen={setIsGroupDropdownOpen}
        showRenameIcon={true}
      >
        {children}
      </AliasEditorWrapper>
    </ChartGroupsUI.DropdownContainer>
  );
};

export default ChartGroupWrapper;
